/**
 * Video carousel: seamless auto-scroll marquee + touch swipe and mouse drag.
 */
class ProductVideoCarousel {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('.product-video-carousel__track');
    this.viewport = root.querySelector('.product-video-carousel__viewport');
    if (!this.track || !this.viewport) return;

    this.loopWidth = 0;
    this.isAutoScrolling = false;
    this.scrollPaused = false;
    this.userInteracting = false;
    this.autoScrollEnabled = true;
    this.resumeTimer = null;
    this.autoScrollFrame = null;
    this.dragActive = false;

    const slideCount = parseInt(root.dataset.slideCount, 10) || 0;
    if (slideCount > 1) {
      this.setupMarquee();
    }

    this.setupAutoplay();
  }

  setupMarquee() {
    const slides = [...this.track.querySelectorAll('.product-video-carousel__slide:not(.product-video-carousel__slide--clone)')];
    if (slides.length < 2) return;

    slides.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.classList.add('product-video-carousel__slide--clone');
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      clone.querySelectorAll('deferred-media').forEach((media) => {
        media.removeAttribute('loaded');
        media.querySelector('video')?.remove();
        media.querySelector('iframe')?.remove();
      });
      this.track.appendChild(clone);
    });

    const duration = parseInt(this.root.dataset.scrollDuration, 10) || 50;
    this.root.style.setProperty('--pvc-marquee-duration', `${duration}s`);
    this.root.classList.add('product-video-carousel--marquee');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.updateLoopWidth(slides);
        if (this.loopWidth <= 0) return;

        this.bindScrollInteraction();
        this.observeVisibility();

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!prefersReducedMotion) {
          this.startAutoScroll(duration);
        }
      });
    });

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateLoopWidth(slides);
      });
      this.resizeObserver.observe(this.track);
    }
  }

  updateLoopWidth(slides) {
    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    let width = 0;
    slides.forEach((slide, index) => {
      width += slide.getBoundingClientRect().width;
      if (index < slides.length - 1) width += gap;
    });
    this.loopWidth = Math.round(width);
  }

  wrapScroll() {
    if (!this.loopWidth) return;

    if (this.viewport.scrollLeft >= this.loopWidth) {
      this.isAutoScrolling = true;
      this.viewport.scrollLeft -= this.loopWidth;
      requestAnimationFrame(() => {
        this.isAutoScrolling = false;
      });
    } else if (this.viewport.scrollLeft < 0) {
      this.isAutoScrolling = true;
      this.viewport.scrollLeft += this.loopWidth;
      requestAnimationFrame(() => {
        this.isAutoScrolling = false;
      });
    }
  }

  observeVisibility() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.autoScrollEnabled = entry.isIntersecting;
        });
      },
      { threshold: 0.08 }
    );
    observer.observe(this.viewport);
  }

  scheduleResume(delay = 1800) {
    clearTimeout(this.resumeTimer);
    this.resumeTimer = window.setTimeout(() => {
      this.userInteracting = false;
      this.scrollPaused = false;
      this.viewport.style.scrollSnapType = '';
    }, delay);
  }

  markUserInteraction() {
    this.userInteracting = true;
    this.scrollPaused = true;
    clearTimeout(this.resumeTimer);
    this.viewport.style.scrollSnapType = 'x mandatory';
  }

  startAutoScroll(durationSec) {
    if (this.autoScrollFrame) cancelAnimationFrame(this.autoScrollFrame);

    let lastTime = null;

    const tick = (time) => {
      if (!this.scrollPaused && !this.userInteracting && this.autoScrollEnabled && lastTime !== null && this.loopWidth > 0) {
        const pxPerMs = this.loopWidth / (durationSec * 1000);
        const delta = time - lastTime;
        this.isAutoScrolling = true;
        this.viewport.scrollLeft += pxPerMs * delta;
        if (this.viewport.scrollLeft >= this.loopWidth) {
          this.viewport.scrollLeft -= this.loopWidth;
        }
        requestAnimationFrame(() => {
          this.isAutoScrolling = false;
        });
      }
      lastTime = time;
      this.autoScrollFrame = requestAnimationFrame(tick);
    };

    this.autoScrollFrame = requestAnimationFrame(tick);
  }

  bindScrollInteraction() {
    const viewport = this.viewport;
    let dragStartX = 0;
    let dragStartScroll = 0;
    let activePointerId = null;

    const isInteractiveTarget = (target) =>
      target.closest('a, button, .deferred-media__poster, input, textarea, select');

    viewport.addEventListener(
      'scroll',
      () => {
        if (this.isAutoScrolling) {
          this.wrapScroll();
          return;
        }
        this.wrapScroll();
        if (this.userInteracting) {
          this.scheduleResume();
        }
      },
      { passive: true }
    );

    viewport.addEventListener(
      'touchstart',
      () => {
        this.markUserInteraction();
      },
      { passive: true }
    );

    viewport.addEventListener(
      'touchend',
      () => {
        this.scheduleResume();
      },
      { passive: true }
    );

    viewport.addEventListener(
      'wheel',
      () => {
        this.markUserInteraction();
        this.scheduleResume();
      },
      { passive: true }
    );

    viewport.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') return;
      if (event.button !== 0) return;
      if (isInteractiveTarget(event.target)) return;

      this.dragActive = true;
      dragStartX = event.clientX;
      dragStartScroll = viewport.scrollLeft;
      activePointerId = event.pointerId;
      this.markUserInteraction();
      viewport.setPointerCapture(activePointerId);
      viewport.classList.add('is-dragging');
    });

    viewport.addEventListener('pointermove', (event) => {
      if (!this.dragActive || event.pointerType === 'touch') return;
      event.preventDefault();
      viewport.scrollLeft = dragStartScroll + (dragStartX - event.clientX);
      this.wrapScroll();
    });

    const endDrag = () => {
      if (!this.dragActive) return;
      this.dragActive = false;
      viewport.classList.remove('is-dragging');
      if (activePointerId !== null) {
        try {
          viewport.releasePointerCapture(activePointerId);
        } catch (_error) {
          /* pointer may already be released */
        }
        activePointerId = null;
      }
      this.scheduleResume();
    };

    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
  }

  setupAutoplay() {
    const slides = this.track.querySelectorAll('.product-video-carousel__slide:not(.product-video-carousel__slide--clone)');
    if (!slides.length) return;

    const target = this.viewport || this.root;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            slides.forEach((slide) => this.playSlide(slide));
          } else {
            slides.forEach((slide) => this.pauseSlide(slide));
          }
        });
      },
      { threshold: 0.12 }
    );

    observer.observe(target);

    slides.forEach((slide) => {
      const media = slide.querySelector('deferred-media.product-video-carousel__deferred');
      if (media) this.bindMutedPlayback(media);
    });
  }

  bindMutedPlayback(media) {
    const poster = media.querySelector('[id^="Deferred-Poster-"]');
    poster?.addEventListener('click', () => {
      window.setTimeout(() => this.enforceMuted(media), 0);
    });

    const observer = new MutationObserver(() => {
      if (media.hasAttribute('loaded')) {
        this.enforceMuted(media);
      }
    });
    observer.observe(media, { attributes: true, attributeFilter: ['loaded'] });
  }

  enforceMuted(media) {
    const video = media.querySelector('video');
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.volume = 0;
    video.setAttribute('muted', '');

    if (!video.dataset.pvcMutedBound) {
      video.dataset.pvcMutedBound = 'true';
      video.addEventListener('volumechange', () => {
        if (!video.muted || video.volume > 0) {
          video.muted = true;
          video.volume = 0;
        }
      });
    }
  }

  playSlide(slide) {
    const media = slide.querySelector('deferred-media.product-video-carousel__deferred');
    if (!media) return;

    const startPlayback = () => {
      this.enforceMuted(media);
      const video = media.querySelector('video');
      if (video) {
        video.playsInline = true;
        const playPromise = video.play();
        if (playPromise) playPromise.catch(() => {});
      }
    };

    if (!media.hasAttribute('loaded') && typeof media.loadContent === 'function') {
      media.loadContent(false);
      const video = media.querySelector('video');
      if (video) {
        if (video.readyState >= 2) {
          startPlayback();
        } else {
          video.addEventListener('loadeddata', startPlayback, { once: true });
        }
      }
      return;
    }

    startPlayback();
  }

  pauseSlide(slide) {
    const video = slide.querySelector('deferred-media.product-video-carousel__deferred video');
    if (video) video.pause();
  }
}

function initProductVideoCarousels(scope = document) {
  scope.querySelectorAll('.product-video-carousel__component').forEach((component) => {
    if (component.dataset.pvcInit) return;
    component.dataset.pvcInit = 'true';
    new ProductVideoCarousel(component);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initProductVideoCarousels());
} else {
  initProductVideoCarousels();
}

document.addEventListener('shopify:section:load', (event) => {
  initProductVideoCarousels(event.target);
});
